/**
 * Zustand store for "opened runs" — runs the user has explicitly viewed.
 *
 * Persisted to localStorage so the pinned section survives page reloads.
 * Capped at MAX_OPENED entries (oldest by openedAt dropped first).
 *
 * RULE 13: no compute; persistence is client-side localStorage only.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BacktestSummary } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_OPENED = 50;
const STORAGE_KEY = 'sl-opened-runs';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OpenedRunEntry {
  run: BacktestSummary;
  openedAt: number;
}

interface OpenedRunsStore {
  opened: OpenedRunEntry[];
  /**
   * Upsert a run into the opened list.
   * If the run is already present it is moved to the front (highest openedAt).
   * Drops the oldest entry when the list exceeds MAX_OPENED.
   */
  markOpened: (run: BacktestSummary) => void;
  /**
   * Remove a run from the opened list by id.
   */
  unpin: (id: string) => void;
  /**
   * Clear the entire opened list (and its persisted localStorage entry —
   * the persist middleware writes through synchronously on `set`).
   * MUST run on logout/login (SHA-71): entries hold full `BacktestSummary`
   * objects for a specific account and are keyed under a fixed storage key
   * (`sl-opened-runs`), not the auth token, so they otherwise survive across
   * accounts on the same browser.
   */
  clearAll: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useOpenedRunsStore = create<OpenedRunsStore>()(
  persist(
    (set) => ({
      opened: [],

      markOpened: (run) =>
        set((state) => {
          // Remove existing entry for this run (upsert semantics)
          const without = state.opened.filter((e) => e.run.id !== run.id);
          // Prepend with current timestamp — newest = first
          const updated: OpenedRunEntry[] = [
            { run, openedAt: Date.now() },
            ...without,
          ];
          // Cap at MAX_OPENED, dropping the oldest (last) entries
          return { opened: updated.slice(0, MAX_OPENED) };
        }),

      unpin: (id) =>
        set((state) => ({
          opened: state.opened.filter((e) => e.run.id !== id),
        })),

      clearAll: () => set({ opened: [] }),
    }),
    {
      name: STORAGE_KEY,
      // Only persist the `opened` array — actions are not serializable
      partialize: (state) => ({ opened: state.opened }),
    }
  )
);
